import React, { useEffect } from 'react';

import Header from '../../components/Header';

import trackPage from '../../utils/trackPage';

import './Approach.css';

function Approach() {
	useEffect(() => {
		document.title = "The Assisted Innovations Digital Foreman Comes With a Full Toolbox";
		trackPage();
	});

	return (
		<>
			<Header>
				<h1>Your Digital Foreman Comes With a Full Toolbox</h1>
			</Header>
			<div className="page-body">
				<div className="step-containers">
					<div>
						<p>Every construction project starts with the right tools and someone who knows how to use them. That's how we approach your digital infrastructure. Five stages, clear purpose at each one, no wasted motion.</p>
					</div>
				</div>
				<div className="step-containers">
					<div className="step-container">
						<h2 className="step-title"><span>Site Survey:</span>&nbsp;<strong><em>Understanding Your Business</em></strong></h2>
						<div className="step-content">
							<p>Before anything gets built, we walk the job site. We dig into how you operate - your workflows, your tools, your team's comfort level with technology, and where the manual work is piling up. We've worked with enough construction and industrial businesses to know what to look for. By the end of this stage, we know exactly what's working, what isn't, and where the real opportunities are.</p>
							<p>We focus on three critical areas:</p>
							<ol>
								<li>
									<h3>How you currently do business</h3>
									<p>We explore your workflows, customer interactions, and day-to-day operations to identify what's working well and where there's room for improvement. This helps us align your website's functionality with your business goals, whether it's streamlining sales, improving customer service, or showcasing your expertise.</p>
								</li>
								<li>
									<h3>Your Team's Comfort Level with Software</h3>
									<p>Not all teams are tech-savvy, and that's okay. We assess your team's familiarity with digital tools to ensure the solutions we deliver are intuitive, accessible, and easy to adopt. Training and support are part of the process if needed.</p>
								</li>
								<li>
									<h3>Identifying Manual Processes</h3>
									<p>Manual workflows can be time-consuming and prone to errors. We'll pinpoint areas where automation can save time, improve accuracy, and reduce effort—whether that's managing inventory, processing orders, or gathering customer data.</p>
								</li>
							</ol>
						</div>
					</div>
					<div className="step-container">
						<h2 className="step-title"><span>Blueprint:</span>&nbsp;<strong><em>Strategic Planning	</em></strong></h2>
						<div className="step-content">
							<p>Once we know the site, we draw the plans. The blueprint covers your website strategy, which tools stay and which get replaced, where processes can be tightened, and what can be automated before we break ground. You get a clear, specific roadmap - not a vague proposal full of agency-speak.</p>
							<p>The strategic plan outlines:</p>
							<ul>
								<li>
									<h3>A website strategy</h3>
									<p>We plan a customized approach that aligns with your business objectives, from creating a seamless user experience to SEO and conversions.</p>
								</li>
								<li>
									<h3>Tools to enhance, replace or remove</h3>
									<p>We recommend updates or replacements to your current technology with an aim towards streamlining your operations and reducing inefficiencies.</p>
								</li>
								<li>
									<h3>Processes that can be enhanced or automated</h3>
									<p>We note the process updates that can help you save time, reduce manual errors, and improve consistency across your workflows.</p> 
								</li>
								<li>
									<h3>New tools or processes to resolve issues</h3>
									<p>Introducing innovative solutions to tackle existing challenges and unlock new opportunities for growth and efficiency.</p>
								</li>
							</ul>
						</div>
					</div>
					<div className="step-container">
						<h2 className="step-title"><span>Build:</span>&nbsp;<strong><em>Implementation</em></strong></h2>
						<div className="step-content">
							<p>Plans approved, work begins. We build or rebuild your website, set up new tools, and put the right processes in place. Where off-the-shelf solutions don't cut it, we build custom. Everything is built to your specs, your workflows, and your team's ability to actually use it.</p>
							<p>Key tasks in this phase may include:</p>
							<ul>
								<li>
									<h3>Building or enhancing your website:</h3>
									<p>We ensure your website operates at peak performance, delivering a user-friendly experience that aligns perfectly with your business objectives and drives results.</p>
								</li>
								<li>
									<h3>Creating custom software solutions:</h3>
									<p>Sometimes your needs can't be fulfilled with an off-the-shelf solution. In these instances we design and build custom software that aligns perfectly with your unique workflows and operational needs.</p>
								</li>
								<li>
									<h3>Updating and creating processes:</h3>
									<p>Time to refine the identified processes and establish any needed new ones to ensure your team operates efficiently, with minimal disruptions and maximum productivity.</p>
								</li>
								<li>
									<h3>Setting up new tools:</h3>
									<p>If the strategy required any new tools - we get them set up and configured to optimize your operations, automate tasks, and resolve the challenges identified in earlier stages.</p>
								</li>
							</ul>
						</div>
					</div>
					<div className="step-container">
						<h2 className="step-title"><span>Finishing:</span>&nbsp;<strong><em>Integration and Optimization</em></strong></h2>
						<div className="step-content">
							<p>Like running electrical and plumbing before the walls close, this is where everything gets connected. Your tools get configured to talk to each other, your data gets cleaned up, and any integrations that need to happen - standard or custom - get done. Nothing goes live until the underlying systems are solid.</p>
							<p>Key tasks in this phase include:</p>
							<ul>
								<li>
									<h3>Data cleanup:</h3>
									<p>We take the data you have and remove duplicates, merge records and add necessary details.</p>
								</li>
								<li>
									<h3>Configuring standard integrations:</h3>
									<p>Setting up essential tools like email marketing platforms, customer relationship management (CRM) systems, and inventory management software to communicate effectively and streamline your operations.</p>
								</li>
								<li>
									<h3>Creating custom integrations:</h3>
									<p>If a necessary integration doesn't have a standard pathway - we implement a tailored one.</p>
								</li>
							</ul>
						</div>
					</div>
					<div className="step-container">
						<h2 className="step-title"><span>Final Walkthrough:</span>&nbsp;<strong><em>Automation and Finalization</em></strong></h2>
						<div className="step-content">
							<p>Before we hand over the keys, we test everything, tighten what needs tightening, and automate whatever's left to automate. We run through every process end-to-end and fix what doesn't work. You get a digital infrastructure that runs - without needing you to babysit it.</p>
							<p>Key tasks in this phase include:</p>
							<ul>
								<li>
									<h3>Automate additional manual processes:</h3>
									<p>While working through stage 3 and 4, more manual processes are often discovered. At this stage we make sure that these are automated as well.</p>
								</li>
								<li>
									<h3>Polish new or optimized processes:</h3>
									<p>Now that we have implemented updates to the processes, we run through them again and fine-tune them to ensure they are seamless, effective, and aligned with your business needs.</p>
								</li>
								<li>
									<h3>Test all processes to ensure optimization:</h3>
									<p>We conduct rigorous testing to identify any issues and make adjustments to guarantee everything runs smoothly and reliably.</p>
								</li>
							</ul>
							<p>By the end of this process, your systems will be running at peak efficiency, supported by robust automation and carefully refined processes. You'll be equipped with a streamlined digital infrastructure designed to save time, reduce errors, and maximize productivity.</p>
						</div>
					</div>
				</div>
				<div className='cta-block'>
					<div className="cta-items">
						<ul>
							<li className='cta-action'>
								<h3>Ready to take your business to the next level?</h3>
								<p>Let us enhance your website to empower your brand and accelerate your evolution. Reach out today to get started.</p>
								<div className="cta-button"><a href="https://calendly.com/ross-assistedinnovations/30min" target="_blank" rel="noreferrer">Schedule a free consultation</a></div>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</>
	)
}

export default Approach;